import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { CycleResponse } from "../../api/client";
import { cn } from "@/lib/utils";
import PnlValue from "../PnlValue";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/states";
import { formatExitReason } from "../positions/closeLabels";
import CycleDetailDrawer from "./CycleDetailDrawer";

interface CycleTableProps {
  rows: CycleResponse[];
  loading: boolean;
  error: boolean;
}

/**
 * Rendering-only table for the History page. Each row expands to reveal the
 * inline {@link CycleDetailDrawer}. Three render states (B5b): loading, error
 * (explicit, never an empty list), rows[] (empty state or populated table).
 */
export default function CycleTable({ rows, loading, error }: CycleTableProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (error) {
    return (
      <ErrorState
        title="Échec du chargement de l'historique"
        testId="history-error"
        className="positions-panel__error p-3"
      >
        <code>GET /api/cycles/history</code> a échoué. L'historique ci-dessous n'est
        pas l'état live — ne t'y fie pas tant que la connexion n'est pas rétablie.
      </ErrorState>
    );
  }
  if (loading) {
    return <LoadingState lines={4} className="p-3" />;
  }
  if (rows.length === 0) {
    return <EmptyState className="p-3">Aucun cycle ne correspond aux filtres.</EmptyState>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="positions-table w-full text-sm whitespace-nowrap">
        <thead>
          <tr className="text-muted-foreground border-border border-b text-left text-[0.68rem] tracking-wide uppercase">
            <th className="px-2 py-2 font-medium">UID</th>
            <th className="px-2 py-2 font-medium">Biais</th>
            <th className="px-2 py-2 font-medium">Statut</th>
            <th className="px-2 py-2 font-medium">Démarré</th>
            <th className="px-2 py-2 font-medium">Terminé</th>
            <th className="px-2 py-2 font-medium">Fin de cycle</th>
            <th className="px-2 py-2 font-medium">Risque €</th>
            <th className="px-2 py-2 font-medium">P&amp;L €</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const expanded = expandedId === c.id;
            return (
              <RowGroup
                key={c.id}
                cycle={c}
                expanded={expanded}
                onToggle={() => setExpandedId(expanded ? null : c.id)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RowGroup({
  cycle,
  expanded,
  onToggle,
}: {
  cycle: CycleResponse;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className="cycle-row--expandable hover:bg-surface-2/60 border-border/60 cursor-pointer border-b font-mono tabular-nums"
        onClick={onToggle}
        data-testid={`cycle-row-${cycle.id}`}
        aria-expanded={expanded}
      >
        <td className="px-2 py-1.5">
          <span className="text-muted-foreground inline-flex items-center gap-1">
            {expanded ? (
              <ChevronDown className="size-3.5" aria-hidden />
            ) : (
              <ChevronRight className="size-3.5" aria-hidden />
            )}
            <code>{cycle.cycleUid ? `${cycle.cycleUid.slice(0, 8)}…` : `#${cycle.id}`}</code>
          </span>
        </td>
        <td
          className={cn(
            "dir px-2 py-1.5 font-semibold",
            cycle.bias === "BUY" ? "dir--buy text-buy" : "dir--sell text-sell",
          )}
        >
          {cycle.bias}
        </td>
        <td className="text-muted-foreground px-2 py-1.5 font-sans">{statusLabel(cycle)}</td>
        <td className="text-muted-foreground px-2 py-1.5">{formatTime(cycle.startedAt)}</td>
        <td className="text-muted-foreground px-2 py-1.5">
          {cycle.endedAt ? formatTime(cycle.endedAt) : "—"}
        </td>
        <td className="text-muted-foreground px-2 py-1.5 font-sans" title={cycle.endReason ?? undefined}>
          {formatExitReason(cycle.endReason)}
        </td>
        <td className="px-2 py-1.5">{Number(cycle.riskBudgetEur).toFixed(2)}</td>
        <td className="px-2 py-1.5">
          <PnlValue
            value={cycle.realizedPnlEur}
            pnlComplete={cycle.pnlComplete}
            withSign
            testId={`row-pnl-${cycle.id}`}
          />
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`cycle-row-${cycle.id}-expanded`} className="bg-bg/40">
          <td colSpan={8} className="p-2">
            <CycleDetailDrawer cycle={cycle} />
          </td>
        </tr>
      )}
    </>
  );
}

function statusLabel(c: CycleResponse): string {
  if (c.endedAt == null) return "en cours";
  if (c.pnlComplete === false) return "clos (partiel)";
  return "clos";
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
